import { toaster } from 'components'
import { startCase } from 'lodash-es'
import { Dispatch, ReactNode, SetStateAction, createContext, useContext, useState } from 'react'
import {
  CreateLeadSessionsProps,
  EndSessionMethod,
  HelperLeadProperties,
  HelperLeadProperty,
  Lead,
  LeadTemperature,
  PrimaryLeadProperties,
  PrimaryLeadProperty,
  SessionMetric,
  SessionTrait,
  SessionWithLead,
  leadTemperatures,
} from 'types'
import { createLeadSessions } from 'utils'
import { dateFromExcel } from 'utils/tableUtils'
import { useEvent } from '../../../utils/eventUtils'

export type AppType = 'custom' | 'explorer'

export type MainPropertyMap = PrimaryLeadProperties & HelperLeadProperties & RootPropertyMap

type RootPropertyMap = Record<'created' | 'deviceName' | 'endMethod', string>

export const rootImportProperties: Array<keyof RootPropertyMap> = [
  'created',
  'deviceName',
  'endMethod',
]

interface SessionImportContextType {
  appTitle: string
  appType: AppType
  changes: CreateLeadSessionsProps
  data: Record<string, string>[] | undefined
  explorerProperties: ExplorerProperties
  mainPropertyMap: MainPropertyMap | undefined
  momentId?: number
  onUpload: () => Promise<void>
  progress: number
  resetState: () => void
  selectedTab: string
  setAppTitle: (appTitle: string) => void
  setAppType: (appType: AppType) => void
  setData: (data: Record<string, string>[] | undefined) => void
  setExplorerProperties: Dispatch<SetStateAction<ExplorerProperties>>
  setMainPropertyMap: Dispatch<SetStateAction<MainPropertyMap | undefined>>
  setMomentId: Dispatch<SetStateAction<number | undefined>>
  setProgress: (progress: number) => void
  setSelectedTab: (selectedTab: string) => void
  setSpreadsheet: (spreadsheet: { file: File; buffer: any } | undefined) => void
  spreadsheet: { file: File; buffer: any } | undefined
}

interface ExplorerProperties {
  traits: string[]
  duration: { registration: string; traits: string; results: string }
  metrics: string[]
}

const SessionImportContext = createContext<SessionImportContextType | undefined>(undefined)

interface Props {
  children: ReactNode
  updateSessions: (body: SessionWithLead[]) => void
}

export function SessionImportProvider({ children, updateSessions }: Props) {
  const { event } = useEvent()

  const [appTitle, setAppTitle] = useState('')
  const [spreadsheet, setSpreadsheet] = useState<{ file: File; buffer: any }>()
  const [selectedTab, setSelectedTab] = useState<string>('')
  const [data, setData] = useState<Record<string, string>[]>()
  const [mainPropertyMap, setMainPropertyMap] = useState<MainPropertyMap>()
  const [momentId, setMomentId] = useState<number>()
  const [explorerProperties, setExplorerProperties] =
    useState<ExplorerProperties>(emptyExplorerProperties)
  const [appType, setAppType] = useState<AppType>('custom')
  const [progress, setProgress] = useState(0)

  const changes = (() => {
    const empty: CreateLeadSessionsProps = []
    if (!mainPropertyMap || !data?.length) return empty

    const { created, deviceName, endMethod, ...leadPropertyMap } = mainPropertyMap
    const inverseLeadPropertyMap: Record<string, PrimaryLeadProperty | HelperLeadProperty> =
      Object.fromEntries(Object.entries(leadPropertyMap).map(([key, value]) => [value, key]))
    const explorerPropertyKeys = [
      ...(explorerProperties?.traits ?? []),
      ...(explorerProperties?.metrics ?? []),
      ...Object.values(explorerProperties?.duration || {}),
    ]

    return data.reduce((acc, row) => {
      const lead = Object.keys(row).reduce((acc, key) => {
        const fieldKey = inverseLeadPropertyMap[key]
        switch (fieldKey) {
          case 'followUp':
          case 'optIn': {
            const v = row[key]
            switch (typeof v) {
              case 'boolean':
                acc[fieldKey] = v
                break
              case 'string':
                const first = v.toLowerCase()[0]
                if (first === 't' || first === 'y' || first === '1') acc[fieldKey] = true
                else if (first === 'f' || first === 'n' || first === '0') acc[fieldKey] = false
                break
              case 'number':
                if (v === 1) acc[fieldKey] = true
                else if (v === 0) acc[fieldKey] = false
                break
            }
            break
          }
          case 'temp':
            const value = row[key]?.toLowerCase()
            if (leadTemperatures.includes(value as LeadTemperature))
              acc[fieldKey] = value as LeadTemperature
            break
          case undefined:
            const isCustom =
              !explorerPropertyKeys.includes(key) &&
              key !== created &&
              key !== deviceName &&
              key !== endMethod
            if (isCustom) {
              if (!acc.custom) acc.custom = {}
              acc.custom[key] = row[key]
            }
            break
          default:
            //@ts-ignore
            acc[fieldKey] = row[key]
        }

        return acc
      }, {} as Lead)

      let update: CreateLeadSessionsProps[0] = {
        appName: 'custom',
        lead,
        eventId: event!.id,
        workspaceId: event!.workspaceId,
        ...(created && { created: dateFromExcel(row[created]) }),
        ...(deviceName && { deviceName: row[deviceName] }),
        ...(endMethod && { endMethod: row[endMethod] as EndSessionMethod }),
      }

      switch (appType) {
        case 'custom': {
          update = { ...update, appTitle: appTitle }
          break
        }
        case 'explorer': {
          const traits = explorerProperties.traits.map<SessionTrait>(trait => ({
            groupTitle: startCase(trait),
            title: row[trait],
          }))
          const actions = explorerProperties.metrics.map<SessionMetric>(key => ({
            groupTitle: '_action',
            title: startCase(key),
            duration: Number(row[key]),
          }))
          const steps = Object.entries(explorerProperties.duration).map<SessionMetric>(
            ([key, value]) => ({
              groupTitle: '_default',
              title: startCase(key),
              duration: Number(value),
            })
          )
          const metrics = [...actions, ...steps].filter(m => Boolean(m.duration))

          update = { ...update, appName: 'explorer', momentId: momentId! }
          if (traits.length) update.traits = traits
          if (metrics.length) update.metrics = metrics
          break
        }
      }

      acc.push(update)
      return acc
    }, empty)
  })()

  async function onUpload() {
    try {
      const updated = await createLeadSessions(changes)
      updateSessions(updated.data)
    } catch (error) {
      console.error(error)
      setProgress(0)
      toaster.create({ title: 'Error uploading file', type: 'error' })
    }
  }

  function resetState() {
    setAppTitle('')
    setSelectedTab('')
    setSpreadsheet(undefined)
    setMainPropertyMap(undefined)
    setExplorerProperties(emptyExplorerProperties)
    setData(undefined)
    setProgress(0)
  }

  return (
    <SessionImportContext.Provider
      value={{
        appTitle,
        appType,
        changes,
        data,
        explorerProperties,
        mainPropertyMap,
        momentId,
        onUpload,
        progress,
        resetState,
        selectedTab,
        setAppTitle,
        setAppType,
        setData,
        setExplorerProperties,
        setMainPropertyMap,
        setMomentId,
        setProgress,
        setSelectedTab,
        setSpreadsheet,
        spreadsheet,
      }}
    >
      {children}
    </SessionImportContext.Provider>
  )
}

const emptyExplorerProperties: ExplorerProperties = {
  traits: [],
  duration: { registration: '', traits: '', results: '' },
  metrics: [],
}

export function useSessionImport() {
  const context = useContext(SessionImportContext)
  if (context === undefined) {
    throw new Error('useSessionImport must be used within a SessionImportProvider')
  }
  return context
}
